// @flow
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Switch, useHistory, Redirect} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {useForm, FormProvider} from 'react-hook-form';
import {
  Typography,
  IconButton,
  Divider,
  Tooltip,
  Hidden,
  InputAdornment,
  Grid,
} from '@material-ui/core';
import {
  ArrowBack,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocalOfferOutlined,
  Search,
} from '@material-ui/icons';
import {Autocomplete} from '@material-ui/lab';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {colors} from '../../../assets/styles/colors';
import {getImageUrl} from '../../../utils/image_provider';

import type {
  User,
  Store,
  Currency,
  ShippingMethodLogistic,
  Coupon,
  Country,
  Order,
  CrmCustomer,
  CreateOrderSummaryRequest,
  TaxInvoiceRequest,
} from '../../../entities';
import {commaFormat} from '../../../utils/number';
import type {ShoppingCart, ShoppingCartItem} from '../duck';
import type {ItemRequest} from '../api';

import {getProductItemOriginalPrice} from '../../../utils/product';
import Collapsible from '../../../elements/Collapsible/Collapsible';
import CustomTextField from '../../../elements/CustomTextField/CustomTextField';

import type {ManualOrderAction, DiscountData} from '../duck';

import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {actions as ManualOrderActions} from '../duck';
// import ShippingForm from './ShippingForm';
import PaymentForm from './PaymentForm';
import {parsePhoneNumber} from 'libphonenumber-js';
import analytics from '../../../analytics';
import LoadingSpinner from '../../../elements/LoadingSpinner/LoadingSpinner';
import searchClient from '../../../assets/img/searchClient.svg';
import CustomModal from '../../../elements/CustomModal/CustomModal';

// import TaxForm from './TaxForm';

import {useIsFeatureInTier} from '../../../utils/component';
import {useDebounce} from '../../../utils/react';
import ControlledTelInput from './ControlledTelInput';

import {INPUT_TYPE} from '../../../entities';

import {useAdditionalInformationContext} from '../../configure/sales-process/context/hooks';
import {useAdditionalInformation} from '../../configure/sales-process/hooks';
import CalendarInput from '../../configure/sales-process/components/Inputs/CalendarInput';
import TextInput from '../../configure/sales-process/components/Inputs/TextInput';
import {ChecklistInput} from '../../configure/sales-process/components/Inputs/ChecklistInput';
import NumberInput from '../../configure/sales-process/components/Inputs/NumberInput';

import type {Dispatch} from 'redux';
import type {RootState} from '../../index';

import type {
  CheckoutInput,
  CartSummaryCoupon,
  CartSummaryItemPromotion,
  CartSummaryItem,
  Summary,
  ShippingAddress,
  CreateOrderRequest,
} from '../../../entities';
import {getProductTaxFactor} from '../../products/utils';
import TaxInvoiceInformationForm, {
  ORDER_TAX_INVOICE_FORM_FIELDS,
} from './TaxInvoiceInformationForm';

import type {PreOrderCheckoutRequest} from '../duck';
import type {Location} from '../api';
import {REGEX} from '../../../constants';
import {useTypedSelector} from '../../../store';

import DeliveryForm from './delivery-form';
import {DELIVERY_FORM_FIELDS} from './delivery-form/constant';
import {useStoreCountry} from '../../country/utils';
import {sanitizeAlphanumeric, StringMutable} from '../../../utils/string';
import {getAdminData} from '../../address/utils/get-admin-data';
import InlineLoader from '../../../elements/LoadingSpinner/InlineLoader';
import {adminResponseToAdminAddress} from '../../address/utils/admin-response-to-admin-address';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import IosSwitch from '../../../elements/IosSwitch/IosSwitch';
import {useStoreTaxInvoiceType} from '../../tax-invoice/utils';
import {sanitizePhoneNumber} from '../../../utils/phone';
import {useEnabledLogisticShipping} from '../../configure/logistics/logistics/utils';

export default connect(
  (state: RootState) => ({
    store: state.store.store,
    shoppingCart: state.manualOrder.shoppingCart,
    itemPrices: state.manualOrder.orderSummary
      ? state.manualOrder.orderSummary.items
      : state.manualOrder.itemPrices,
    currency: state.currency.currency,
    country: state.country.country,
    shippingMethod: state.logisticsConfig.shippingMethod,
    pickupMethod: state.logisticsConfig.pickupMethod,
    orderSummary: state.manualOrder.orderSummary,
    subTotal: state.manualOrder.subTotal,
    total: state.manualOrder.total,
    promotions: state.manualOrder.promotions,
    coupon: state.manualOrder.coupon,
    isExecutingCheckout: state.manualOrder.isExecutingCheckout,
    createOrderSuccess: state.manualOrder.createOrderSuccess,
    order: state.manualOrder.order,
    customers: state.manualOrder.customers,
    globalDiscount: state.manualOrder.globalDiscountData,
  }),
  (dispatch: Dispatch<any>) =>
    bindActionCreators(
      {
        ...ManualOrderActions,
      },
      dispatch
    )
)(ManualOrderCheckout);

type Props = {
  store?: Store,
  user?: User,
  currency: Currency,
  country?: Country,
  shoppingCart: ShoppingCart,
  fetchOrderSummary: (payload: ManualOrderAction) => void,
  itemPrices?: CartSummaryItem[],
  shippingMethod?: ShippingMethodLogistic,
  pickupMethod?: any,
  coupons?: Coupon[],
  isFetchingCoupons?: boolean,
  fetchCoupons: () => void,
  subTotal?: string,
  total?: string,
  coupon?: CartSummaryCoupon,
  promotions?: CartSummaryItemPromotion[],
  orderSummary?: Summary,
  createOrder: ({checkoutRequest: CreateOrderRequest}) => void,
  createPreOrder: ({checkoutRequest: PreOrderCheckoutRequest}) => void,
  isExecutingCheckout?: boolean,
  createOrderSuccess?: boolean,
  order?: Order,
  resetProductsState: () => void,
  searchCustomers: (payload: ManualOrderAction) => void,
  customers?: CrmCustomer[],
  globalDiscount?: DiscountData,
};

const useStyles = makeStyles(theme => ({
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(4, 4, 0, 4),
    background: '#f8f9fb',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 4, 0, 4),
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2, 0, 2),
    },
  },
  textPurple: {
    color: colors.purple,
  },
  shoppingCartSubtotal: {
    color: colors.grey800,
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  preOrder: {
    maxWidth: 776,
    margin: theme.spacing(2, 4, 0, 4),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2, 2, 0, 2),
    },
  },
  preOrderContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '32px',
    [theme.breakpoints.up('sm')]: {
      flexWrap: 'nowrap',
      justifyContent: 'space-between',
    },
  },
  preOrderText: {
    flexGrow: 1,
    fontSize: '0.875rem',
    color: colors.grey800,
  },
  preOrderButton: {
    background: colors.lightPurple,
    height: 40,
    width: 300,
    fontSize: '1rem',
    fontWeight: 600,
  },
  content: {
    // marginTop: theme.spacing(3),
    // flexGrow: 1,
    display: 'flex',
    alignItems: 'flex-start',
    maxWidth: 840,
    gap: '16px',
    // flexDirection: 'column',
    // overflowY: 'auto',
    // height: '100%',
    // background: '#f8f9fb',
    // overflow: 'auto',
    padding: theme.spacing(0, 4, 4, 4),
    [theme.breakpoints.only('sm')]: {
      marginBottom: 64,
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(0, 2, 2, 2),
      marginBottom: 180,
    },
  },
  columnContainer: {
    width: '100%',
    // maxWidth: '100%',
  },
  contentColumn: {
    // width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    borderRadius: '12px',
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      maxWidth: 'unset',
      marginRight: 'unset',
      marginBottom: theme.spacing(2),
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    maxWidth: 400,
    // '&:last-child': {
    //   marginRight: 'unset',
    // },
  },
  contentCollapsible: {
    width: '100%',
    position: 'relative',
  },
  loader: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    backdropFilter: 'blur(.125rem)',
    background: '#ffffff78',
  },
  sectionTitleWrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontWeight: 600,
    color: colors.grey900,
  },
  sectionOptional: {
    fontSize: '0.875rem',
    color: colors.grey600,
  },
  checkoutCartFooterMobile: {
    position: 'fixed',
    width: '100%',
    height: '140px',
    background: 'white',
    padding: '16px 20px',
    borderRadius: '16px 16px 0px 0px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    bottom: 64,
    left: 0,
    boxShadow: ' 0px -3px 14px rgba(24, 39, 75, 0.12)',
    transition: 'height 0.3s ease-in-out',
    zIndex: 10,
  },
  checkoutCartFooterMobileOpen: {
    height: 'calc(100vh - 80px)',
    zIndex: 9999,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  footerMobileText: {
    color: colors.grey700,
  },
  checkoutTotal: {
    color: colors.grey800,
    fontWeight: 600,
  },
  footerMobileCollapse: {
    padding: '0px',
  },
  sectionTitleSummary: {
    borderRadius: '12px',
    background: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 0, 2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
  },
  shoppingCartItemsAmount: {
    marginLeft: 'auto',
    backgroundColor: colors.grey200,
    borderRadius: '12px',
    padding: '4px 12px',
    fontWeight: 600,
  },
  deliveryRadio: {
    // margin: theme.spacing(1, 0),
  },
  deliveryRadioLabel: {
    fontWeight: 600,
  },
  deliveryRadioIcon: {
    // padding: 0,
    // marginRight: 12,
  },
  pickupTextBlock: {
    paddingLeft: 32,
  },
  pickupText: {
    color: colors.grey600,
    fontSize: '0.875rem',
    whiteSpace: 'pre-wrap',
  },

  option: {
    fontSize: '14px',
    color: colors.grey800,
  },
  optionSelected: {
    color: colors.purple,
  },
  textField: {
    '&:not(:last-child)': {
      marginBottom: theme.spacing(2),
    },
  },

  summaryLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  summaryText: {
    display: 'flex',
  },
  couponDiscountChip: {
    display: 'flex',
    alignItems: 'center',
    background: colors.error,
    color: 'white',
    borderRadius: '24px',
    padding: '2px 8px',
    fontSize: '0.75rem',
    fontWeight: 500,
    marginRight: 8,
  },
  summaryTextCoupon: {
    color: colors.success,
    whiteSpace: 'nowrap',
    maxWidth: '30ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
  bold: {
    fontWeight: 700,
  },
  continueButton: {
    marginTop: theme.spacing(1),
    height: 40,
    fontWeight: 600,
    fontSize: '1.125rem',
  },

  coupon: {
    width: '100%',
    justifyContent: 'space-between',
    borderRadius: '8px',
    background: colors.grey200,
    height: 44,
    '&:not(:last-child)': {
      marginBottom: theme.spacing(1),
    },
  },
  couponApplied: {
    border: `2px solid ${colors.purple3}`,
  },
  couponCode: {
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  couponApply: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.purple3,
  },
  couponRemove: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.error,
  },

  divider: {
    background: colors.grey200,
    width: '100%',
  },

  subtitle: {
    fontWeight: 600,
  },
  itemContainer: {
    height: '100%',
  },
  shoppingCartItem: {
    margin: theme.spacing(2, 0),
    '&:last-child': {
      marginBottom: 0,
    },
    display: 'flex',
    // alignItems: 'center',
    position: 'relative',
  },
  shoppingCartProductDetail: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-around',
    marginRight: -36,
  },
  shoppingCartProductImage: {
    borderRadius: '12px',
    height: 64,
    width: 64,
    marginRight: 16,
  },
  shoppingCartProductName: {
    fontSize: '0.875rem',
    display: '-webkit-box',
    overflow: 'hidden',
    lineClamp: 2,
    '-webkit-box-orient': 'vertical',
  },
  shoppingCartProductVariant: {
    fontSize: '0.875rem',
    color: colors.grey700,
    fontWeight: 500,
  },
  shoppingCartProductOptions: {
    fontSize: '0.75rem',
    color: colors.grey600,
    fontWeight: 500,
  },
  shoppingCartProductAmount: {
    fontSize: '0.875rem',
    color: colors.grey600,
  },
  shoppingCartItemActions: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  shoppingCartItemDeleteButton: {
    color: colors.error,
  },
  shoppingCartProductBulkPriceFlag: {
    // right: 6,
    padding: 6,
    color: '#158CFD',
    background: '#E9F4FF',
    marginRight: 8,
    display: 'flex',
    borderRadius: '20px',
  },
  shoppingCartProductPrice: {
    textAlign: 'right',
  },
  shoppingCartProductPriceNormal: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 500,
    color: colors.grey800,
    whiteSpace: 'nowrap',
  },
  shoppingCartProductPriceDiscounted: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey600,
    whiteSpace: 'nowrap',
    textDecoration: 'line-through',
  },
  summarySubtitle: {
    margin: theme.spacing(2, 0, 1, 0),
    color: colors.grey800,
    fontWeight: 500,
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  summaryInfo: {
    fontSize: '0.875rem',
    color: colors.grey800,
  },
  createPreOrderModal: {
    marginTop: theme.spacing(2),
    width: 300,
  },
  createPreOrderModalText: {
    textAlign: 'center',
    fontWeight: 600,
  },
  error: {
    margin: 0,
    color: '#f44336',
    fontSize: '0.75rem',
  },
}));

type SearchParams = {
  fullName?: string,
  phone?: string,
};

export type ManualOrderShippingData = {
  address1: string,
  address2?: string,
  admin1?: string,
  admin2?: string,
  admin3?: string,
  location?: Location,
};

const buildQueryParams = (queryParams: SearchParams) => {
  const params: {[key: string]: any} = {
    size: '8',
    page: 0,
  };
  if (queryParams.fullName) {
    params.fullName = queryParams.fullName;
  }
  if (queryParams.phone) {
    params.phone = queryParams.phone;
  }
  return params;
};

function ManualOrderCheckout(props: Props) {
  const {
    store,
    currency,
    country,
    shoppingCart,
    fetchOrderSummary,
    itemPrices,
    shippingMethod,
    orderSummary,
    subTotal,
    total,
    coupon,
    promotions,
    createOrder,
    createPreOrder,
    isExecutingCheckout,
    createOrderSuccess,
    order,
    resetProductsState,
    searchCustomers,
    customers = [],
    globalDiscount,
  } = props;

  const classes = useStyles();

  const storeCountry = useStoreCountry();

  const formInformationValues = useRef({});

  const [step, setStep] = useState<number>(0);

  const history = useHistory();

  const methods = useForm();

  const {
    register,
    handleSubmit,
    errors,
    watch,
    setValue,
    reset,
    unregister,
  } = methods;

  const {
    additionalInformation: addInfoContextValues,
    setAdditionalInformationValidate,
  } = useAdditionalInformationContext();

  const {
    additionalInformation,
    identityNumberInput,
  } = useAdditionalInformation();

  const hasCustomerFeature = useIsFeatureInTier('CRM_CUSTOMER');

  const {deliveryMethods, getDeliveryMethods} = useEnabledLogisticShipping();

  useEffect(() => {
    getDeliveryMethods();
  }, [getDeliveryMethods]);

  const isDeliveryEnabled =
    deliveryMethods?.shippingMethod?.enabled ||
    deliveryMethods?.pickupMethod?.enabled;

  const [checkoutMobileOpen, setCheckoutMobileOpen] = useState(false);
  const openCheckoutMobile = () => {
    setCheckoutMobileOpen(true);
  };
  const closeCheckoutMobile = () => {
    setCheckoutMobileOpen(false);
  };

  const [paymentMethodSelected, setPaymentMethodSelected] = useState('');

  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState(null);
  const firstNameRef = useRef(false);

  const [phoneToSearch, setPhoneToSearch] = useState('');
  const [customerSelected, setCustomerSelected] = useState<CrmCustomer | null>(
    null
  );

  const {storeTaxInvoiceType} = useStoreTaxInvoiceType();
  const hasTaxInvoice = storeTaxInvoiceType.length > 0;

  const [taxFormOpen, setTaxFormOpen] = useState(false);

  const handleTaxFormOpenChange = () => {
    setTaxFormOpen(prevOpen => {
      if (prevOpen) {
        unregister(ORDER_TAX_INVOICE_FORM_FIELDS.taxInvoiceType);
        unregister(ORDER_TAX_INVOICE_FORM_FIELDS.companyName);
        unregister(ORDER_TAX_INVOICE_FORM_FIELDS.ruc);
        unregister(ORDER_TAX_INVOICE_FORM_FIELDS.address);
        unregister(ORDER_TAX_INVOICE_FORM_FIELDS.admin1);
        unregister(ORDER_TAX_INVOICE_FORM_FIELDS.admin2);
        unregister(ORDER_TAX_INVOICE_FORM_FIELDS.admin3);
      }

      return !prevOpen;
    });
  };

  const [
    customerDeliveryData,
    setCustomerDeliveryData,
  ] = useState<ShippingAddress | null>(null);

  const [fetchingDeliveryData, setFetchingDeliveryData] = useState(false);

  const [firstName, setFirstName] = useState({
    value: '',
    fromAutoComplete: false,
  });

  const tempOrderInfo = useTypedSelector(
    state => state.manualOrder.tempOrderInfo
  );

  // const [_, setTempOrderInfo] = useState<any>(null);

  const phoneDebounced = useDebounce(phoneToSearch, 500);
  const firstNameDebounced = useDebounce(firstName, 500);

  const deliveryData = watch([
    DELIVERY_FORM_FIELDS.SHIPPING_RATE_ID,
    DELIVERY_FORM_FIELDS.ADDRESS_1,
    DELIVERY_FORM_FIELDS.ADDRESS_2,
    DELIVERY_FORM_FIELDS.ADMIN1,
    DELIVERY_FORM_FIELDS.ADMIN2,
    DELIVERY_FORM_FIELDS.ADMIN3,
    DELIVERY_FORM_FIELDS.ZIP_CODE,
    DELIVERY_FORM_FIELDS.COUNTRY,
    DELIVERY_FORM_FIELDS.DELIVERY_METHOD,
    DELIVERY_FORM_FIELDS.PICKUP_METHOD,
  ]);
  // const deliveryMethodForm = deliveryData[DELIVERY_FORM_FIELDS.DELIVERY_METHOD];

  /*   const deliveryDataValid = useMemo(() => {
    const address1 = deliveryData[DELIVERY_FORM_FIELDS.ADDRESS_1];
    const country =
      deliveryData[DELIVERY_FORM_FIELDS.COUNTRY]?.code || storeCountry?.code;
    const admin1 = deliveryData[DELIVERY_FORM_FIELDS.ADMIN1]?.adminId;
    const admin2 = deliveryData[DELIVERY_FORM_FIELDS.ADMIN2]?.adminId;
    const shippingRateId = deliveryData[DELIVERY_FORM_FIELDS.SHIPPING_RATE_ID];

    const deliveryMethod = deliveryData[DELIVERY_FORM_FIELDS.DELIVERY_METHOD];

    const isShipping = deliveryMethod === 'SHIPPING';

    const isDeliveryDataValid = isShipping
      ? shippingRateId && address1 && admin1 && admin2 && country
      : shippingRateId;

    return Boolean(isDeliveryDataValid);
  }, [deliveryData, storeCountry]); */

  const buildOrderSummaryRequest = useCallback(() => {
    if (
      !store ||
      !shoppingCart ||
      !shoppingCart.items ||
      !storeCountry ||
      (isDeliveryEnabled && !shippingMethod)
    ) {
      return null;
    }

    const shoppingCartItems = shoppingCart.items.map(
      (item: ShoppingCartItem) => {
        const product = item.product;
        const productVariant = item.productVariant;

        return {
          productId: product.id,
          productVariantId: productVariant ? productVariant.id : undefined,
          productOptions: item.productOptions,
          quantity: item.amount,
        };
      }
    );

    const coupon = shoppingCart.couponCode || undefined;

    // Only include deliveryData if delivery is enabled
    if (isDeliveryEnabled) {
      const address1 = deliveryData[DELIVERY_FORM_FIELDS.ADDRESS_1];
      const address2 = deliveryData[DELIVERY_FORM_FIELDS.ADDRESS_2];
      const zipCode = deliveryData[DELIVERY_FORM_FIELDS.ZIP_CODE];
      const country =
        deliveryData[DELIVERY_FORM_FIELDS.COUNTRY]?.code || storeCountry.code;
      const admin1 = deliveryData[DELIVERY_FORM_FIELDS.ADMIN1]?.adminId;
      const admin2 = deliveryData[DELIVERY_FORM_FIELDS.ADMIN2]?.adminId;
      const admin3 = deliveryData[DELIVERY_FORM_FIELDS.ADMIN3]?.adminId;
      const shippingRateId =
        deliveryData[DELIVERY_FORM_FIELDS.SHIPPING_RATE_ID];
      const pickupMethod = deliveryData[DELIVERY_FORM_FIELDS.PICKUP_METHOD];
      const deliveryMethod = deliveryData[DELIVERY_FORM_FIELDS.DELIVERY_METHOD];

      const isShipping = deliveryMethod === 'SHIPPING';
      const pickUpRateId = pickupMethod?.shippingRates[0]?.id;

      const isDeliveryDataValid = isShipping
        ? shippingRateId && address1 && admin1 && admin2 && country
        : !!pickupMethod;

      if (!isDeliveryDataValid) {
        return null;
      }

      const orderSummaryRequest: CreateOrderSummaryRequest = {
        items: shoppingCartItems,
        deliveryData: {
          deliveryMethod,
          shippingRateId: isShipping ? shippingRateId : pickUpRateId,
          shippingMethodType:
            isShipping &&
            shippingMethod &&
            shippingMethod.storeShippingMethodType !== 'MANUAL'
              ? shippingMethod.storeShippingMethodType
              : undefined,
        },
        globalDiscount: shoppingCart.globalDiscount,
        coupon,
      };

      if (isShipping) {
        orderSummaryRequest.deliveryData.shippingAddress = {
          address1,
          address2: address2 ? address2 : undefined,
          admin1: Number(admin1),
          admin2: Number(admin2),
          admin3: admin3 ? Number(admin3) : undefined,
          country: country,
          zipCode: zipCode ? zipCode : undefined,
        };
      }

      return orderSummaryRequest;
    }

    // When delivery is disabled, return null to skip order summary request
    return null;
  }, [
    deliveryData,
    shippingMethod,
    shoppingCart,
    store,
    storeCountry,
    isDeliveryEnabled,
  ]);

  const prevOrderSummaryRequestRef = useRef<CreateOrderSummaryRequest | null>(
    null
  );
  useEffect(() => {
    const orderSummaryRequest: CreateOrderSummaryRequest | null = buildOrderSummaryRequest();
    const prevOrderSummaryRequest = prevOrderSummaryRequestRef.current;
    if (
      JSON.stringify(orderSummaryRequest) !==
      JSON.stringify(prevOrderSummaryRequest)
    ) {
      prevOrderSummaryRequestRef.current = orderSummaryRequest;
      if (orderSummaryRequest) {
        fetchOrderSummary({orderSummaryRequest});
      }
    }
  }, [buildOrderSummaryRequest, fetchOrderSummary]);

  const getCOItems = (): ItemRequest[] => {
    const result = shoppingCart.items.map(
      (item: ShoppingCartItem, i: number) => {
        return {
          productId: item.product.id,
          productVariantId: item.productVariant
            ? item.productVariant.id
            : undefined,
          quantity: item.amount,
          productOptions: item.productOptions,
        };
      }
    );
    return result;
  };

  const filteredAdditionalInformation = useMemo(() => {
    if (!additionalInformation || additionalInformation.length === 0)
      return undefined;
    return additionalInformation?.filter(
      el => el.id !== identityNumberInput?.id
    );
  }, [additionalInformation, identityNumberInput]);

  const isValidCustomAdditionalInformation = () => {
    let isValid = true;

    for (let id in addInfoContextValues) {
      if (!isValid) return;
      const obj = addInfoContextValues[id];
      if (!!obj.error) isValid = false;
    }
    return isValid;
  };

  const buildAdditionalInformationRaw = () => {
    const addInfoArray = [];
    for (let addInfoId in addInfoContextValues) {
      const elem = addInfoContextValues[addInfoId];
      const value = elem.value ?? [];
      const hasValues = value.filter(el => !!el).length > 0;
      const additionalInformationField = additionalInformation?.find(
        el => el.id === addInfoId
      );

      if (hasValues)
        addInfoArray.push({
          storeOrderAdditionalInformationId: addInfoId,
          additionalInformation: {
            value: addInfoContextValues[addInfoId].value,
            number: additionalInformationField
              ? additionalInformationField.number
              : 0,
          },
        });
    }
    return addInfoArray;
  };

  const buildTextInfo = (data: {[key: string]: any}) => {
    return Object.entries(data)
      .filter(
        ([key, value]: [any, any]) => key.startsWith('AI-') && value?.length > 0
      )
      .map(([key, value]) => {
        const additionalInformationField = additionalInformation?.find(
          el => el.id === key
        );
        return {
          storeOrderAdditionalInformationId: key,
          additionalInformation: {
            value: [value],
            number: additionalInformationField
              ? additionalInformationField.number
              : 0,
          },
        };
      });
  };

  const manualOrderShippingData:
    | ManualOrderShippingData
    | typeof undefined = useMemo(() => {
    const formValues = formInformationValues.current;
    const address = formValues[DELIVERY_FORM_FIELDS.ADDRESS_1];
    const admin1 = formValues[DELIVERY_FORM_FIELDS.ADMIN1]?.adminName;
    const admin2 = formValues[DELIVERY_FORM_FIELDS.ADMIN2]?.adminName;
    const admin3 = formValues[DELIVERY_FORM_FIELDS.ADMIN3]?.adminName;
    const shippingData: ManualOrderShippingData = {
      address1: address,
      admin1,
      admin2,
      admin3,
    };

    return shippingData;
  }, []);

  const handleCreateOrder = data => {
    if (isDeliveryEnabled && !shippingMethod) return;
    const formValues = formInformationValues.current;
    const registeredValues = buildTextInfo(formValues);
    let additionalInformation = [
      ...buildAdditionalInformationRaw(),
      ...registeredValues,
    ];

    const documentNumberInputId = identityNumberInput?.id;
    if (documentNumberInputId && formValues.idNumber) {
      additionalInformation = [
        ...additionalInformation,
        {
          storeOrderAdditionalInformationId: documentNumberInputId,
          additionalInformation: {
            value: [formValues.idNumber],
            number: additionalInformation.length,
          },
        },
      ];
    }

    const checkoutRequest: CreateOrderRequest = {
      orderData: {
        items: getCOItems() || [],
        customer: {
          email: formValues.email,
          firstName: firstName.value,
          identificationNumber: formValues.idNumber || undefined,
          identificationType: 'DNI',
          lastName: formValues.lastName,
          phone: sanitizePhoneNumber(phone),
        },
        coupon: shoppingCart.couponCode || undefined,
        //$FlowFixMe
        additionalInformation,
        globalDiscount: shoppingCart.globalDiscount,
      },
      paymentData: {
        storePaymentMethodProcessorId: paymentMethodSelected || '',
      },
    };

    // Only include deliveryData if delivery is enabled
    if (isDeliveryEnabled) {
      const address1 = formValues[DELIVERY_FORM_FIELDS.ADDRESS_1];
      const address2 = formValues[DELIVERY_FORM_FIELDS.ADDRESS_2];
      const zipCode = formValues[DELIVERY_FORM_FIELDS.ZIP_CODE];
      const country =
        formValues[DELIVERY_FORM_FIELDS.COUNTRY]?.code || storeCountry.code;
      const admin1 = formValues[DELIVERY_FORM_FIELDS.ADMIN1]?.adminId;
      const admin2 = formValues[DELIVERY_FORM_FIELDS.ADMIN2]?.adminId;
      const admin3 = formValues[DELIVERY_FORM_FIELDS.ADMIN3]?.adminId;

      const deliveryMethod = formValues[DELIVERY_FORM_FIELDS.DELIVERY_METHOD];
      const pickupMethod = formValues[DELIVERY_FORM_FIELDS.PICKUP_METHOD];
      const shippingRateId = formValues[DELIVERY_FORM_FIELDS.SHIPPING_RATE_ID];
      const manualDeliveryCost =
        formValues[DELIVERY_FORM_FIELDS.MANUAL_DELIVERY_COST];
      const isShipping = deliveryMethod === 'SHIPPING';

      console.log({formValues});

      const pickUpRateId = pickupMethod?.shippingRates[0]?.id;

      const deliveryData = {
        deliveryMethod,
        shippingRateId: isShipping ? shippingRateId : pickUpRateId,
        manualDeliveryCost: manualDeliveryCost ? manualDeliveryCost : undefined,
        shippingMethodType:
          isShipping && shippingMethod
            ? shippingMethod.storeShippingMethodType
            : undefined,
        ...(isShipping && {
          shippingAddress: {
            address1,
            address2: address2 ? address2 : undefined,
            admin1: Number(admin1),
            admin2: Number(admin2),
            admin3: admin3 ? Number(admin3) : undefined,
            country: country,
            zipCode: zipCode ? zipCode : undefined,
          },
        }),
      };

      checkoutRequest.deliveryData = deliveryData;
    }

    if (data && data[ORDER_TAX_INVOICE_FORM_FIELDS.taxInvoiceType]) {
      const taxInvoiceRequest: TaxInvoiceRequest = {
        taxInvoiceType: data[ORDER_TAX_INVOICE_FORM_FIELDS.taxInvoiceType],
        taxReceiverName: data[ORDER_TAX_INVOICE_FORM_FIELDS.companyName],
        country: country?.code || 'PE',
        taxReceiverCode: data[ORDER_TAX_INVOICE_FORM_FIELDS.ruc],
        address: {
          address: sanitizeAlphanumeric(
            data[ORDER_TAX_INVOICE_FORM_FIELDS.address]
          ),
          admin1: data[ORDER_TAX_INVOICE_FORM_FIELDS.admin1],
          admin2: data[ORDER_TAX_INVOICE_FORM_FIELDS.admin2],
          admin3: data[ORDER_TAX_INVOICE_FORM_FIELDS.admin3],
          country: country?.code || 'PE',
        },
      };
      checkoutRequest.orderTaxInvoiceInformationData = taxInvoiceRequest;
    }

    createOrder({checkoutRequest});
  };

  const [confirmPreOrderModal, setConfirmPreOrderModal] = useState(false);
  const handleCreatePreOrder = () => {
    const items = getCOItems();
    if (items) {
      setConfirmPreOrderModal(true);
    }
  };

  const confirmCreatePreOrder = () => {
    const items = getCOItems();
    const checkoutRequest: PreOrderCheckoutRequest = {
      items,
      coupon: shoppingCart.couponCode || undefined,
      globalDiscount: shoppingCart.globalDiscount,
    };
    createPreOrder({checkoutRequest});
  };

  const userHasPreOrderFeature = useIsFeatureInTier('PRE_ORDER');

  useEffect(() => {
    if (order && order.id && createOrderSuccess) {
      analytics.fireCustomEvent(
        'Manual Order Created',
        undefined,
        ['user-guiding'],
        () => history.push('/orders/manual-order/success')
      );
    }
  }, [createOrderSuccess, history, order]);

  //search by name
  useEffect(() => {
    if (firstNameDebounced.value.trim().length === 0) return;
    if (firstNameDebounced.fromAutoComplete) return;

    const fetchCustomersCallback = (searchString?: string) => {
      const searchFilter = buildQueryParams({fullName: searchString});
      if (searchFilter && hasCustomerFeature) {
        searchCustomers({searchFilter});
      }
    };

    fetchCustomersCallback(firstNameDebounced.value);
  }, [firstNameDebounced, hasCustomerFeature, searchCustomers]);

  //search by phone
  useEffect(() => {
    if (phoneDebounced.trim().length === 0) return;

    const fetchCustomersCallback = (searchString?: string) => {
      const searchFilter = buildQueryParams({phone: searchString});
      if (searchFilter && hasCustomerFeature) {
        searchCustomers({searchFilter});
      }
    };

    fetchCustomersCallback(phoneDebounced);
  }, [phoneDebounced, hasCustomerFeature, searchCustomers]);

  const shoppingCartItemsAmount = useMemo(() => {
    if (shoppingCart && shoppingCart.items) {
      let result = 0;
      shoppingCart.items.forEach(i => {
        result += i.amount;
      });
      return result;
    }
  }, [shoppingCart]);

  const shoppingCartItemsSection = shoppingCart &&
    shoppingCart.items &&
    shoppingCart.items.length && (
      <Collapsible
        className={classes.contentCollapsible}
        title={
          <div className={classes.sectionTitleWrapper}>
            <Typography className={classes.sectionTitle}>
              Resumen de Productos
            </Typography>
            <div className={classes.shoppingCartItemsAmount}>
              {shoppingCartItemsAmount}
            </div>
          </div>
        }
        paddingH={2}
        // startClosed={}
      >
        {shoppingCart.items.map((item: ShoppingCartItem, i) => {
          let productPrice = {unitPrice: getProductItemOriginalPrice(item)};
          let taxFactor = 1;
          let productOptionsNames = [];
          if (item && itemPrices && itemPrices.length) {
            const index = itemPrices.findIndex(price =>
              item.productVariant
                ? price.item.productVariant === item.productVariant.id
                : price.item.product === item.product.id
            );
            const draftItem = itemPrices[index];
            if (index >= 0 && draftItem.item.price)
              productPrice = draftItem.item.price;
            taxFactor = getProductTaxFactor(draftItem.taxes ?? []);

            if (draftItem?.item?.detail?.productOptions) {
              for (const option of draftItem.item.detail.productOptions) {
                productOptionsNames.push(option.name);
              }
            }
          }
          return (
            <React.Fragment key={i}>
              <div className={`${classes.shoppingCartItem}`}>
                <img
                  className={classes.shoppingCartProductImage}
                  src={getImageUrl(
                    item.productVariant
                      ? item.productVariant.imageId
                      : item.product.mainImageId
                  )}
                  alt=""
                />
                <div className={classes.shoppingCartProductDetail}>
                  <Typography className={classes.shoppingCartProductName}>
                    {item.product.name}
                  </Typography>
                  {item.productVariant && item.productVariant.options && (
                    <Typography className={classes.shoppingCartProductVariant}>
                      {Object.values(item.productVariant.options).join('/')}
                    </Typography>
                  )}
                  {item.productVariant && item.productVariant.combinations && (
                    <Typography className={classes.shoppingCartProductVariant}>
                      {Object.values(item.productVariant.combinations)
                        .join('/')
                        .toLowerCase()}
                    </Typography>
                  )}
                  {productOptionsNames.length > 0 && (
                    <Typography className={classes.shoppingCartProductOptions}>
                      {'• '}
                      {productOptionsNames.join(' • ')}
                    </Typography>
                  )}
                  <Typography className={classes.shoppingCartProductAmount}>
                    Cantidad: {item.amount}
                  </Typography>
                </div>
                <div className={classes.shoppingCartItemActions}>
                  <div />
                  <div className={classes.shoppingCartProductPrice}>
                    {parseFloat(productPrice.unitPrice) <
                      (item.productVariant
                        ? parseFloat(item.productVariant.price)
                        : parseFloat(item.product.price)) && (
                      <Typography
                        className={classes.shoppingCartProductPriceDiscounted}
                      >
                        {commaFormat(
                          (item.productVariant
                            ? parseFloat(item.productVariant.price) * taxFactor
                            : parseFloat(item.product.price)) *
                            item.amount *
                            taxFactor,
                          currency ? currency.symbol : ''
                        )}
                      </Typography>
                    )}
                    <Typography
                      className={classes.shoppingCartProductPriceNormal}
                    >
                      {productPrice.source === 'BULK_PRICE' && (
                        <Tooltip title="Precio al por mayor aplicado" arrow>
                          <div
                            className={classes.shoppingCartProductBulkPriceFlag}
                          >
                            <LocalOfferOutlined fontSize="small" />
                          </div>
                        </Tooltip>
                      )}
                      {item.product && item.amount
                        ? commaFormat(
                            parseFloat(productPrice.unitPrice) *
                              item.amount *
                              taxFactor,
                            currency ? currency.symbol : ''
                          )
                        : 0}
                    </Typography>
                  </div>
                </div>
              </div>
              {i + 1 < shoppingCart.items.length && (
                <Divider className={classes.divider} />
              )}
            </React.Fragment>
          );
        })}
      </Collapsible>
    );

  const promotionApplieds = promotions && promotions.length ? promotions : [];

  const subTotalCost = useMemo(() => {
    return subTotal
      ? subTotal
      : orderSummary?.subtotal
      ? orderSummary.subtotal
      : '';
  }, [orderSummary, subTotal]);

  const manualDeliveryCost =
    watch('manualDeliveryCost') ??
    formInformationValues.current.manualDeliveryCost;

  const deliveryCost = useMemo(() => {
    if (!isDeliveryEnabled) return '0';

    return tempOrderInfo && tempOrderInfo.manualDeliveryCost
      ? tempOrderInfo.manualDeliveryCost
      : !!manualDeliveryCost
      ? manualDeliveryCost
      : orderSummary?.shipping
      ? orderSummary.shipping
      : '';
  }, [manualDeliveryCost, orderSummary, tempOrderInfo, isDeliveryEnabled]);

  const shippingPromotionDiscount = useMemo(() => {
    if (!isDeliveryEnabled) return 0;

    return orderSummary?.shippingPromotionDiscount
      ? Math.abs(parseFloat(orderSummary?.shippingPromotionDiscount))
      : 0;
  }, [orderSummary, isDeliveryEnabled]);

  const summaryDiscounts = useMemo(() => {
    if (promotionApplieds.length) {
      const ammount = promotionApplieds.reduce(
        (acum, val) => acum + parseFloat(val.discount),
        0
      );
      return ammount;
    }
    if (orderSummary && orderSummary.couponDiscount)
      return orderSummary.couponDiscount;

    return 0;
  }, [orderSummary, promotionApplieds]);

  const totalCost = useMemo(() => {
    if (!subTotal) return total || '';
    if (isDeliveryEnabled && !deliveryCost) return total || '';

    const subTotalPrice = parseFloat(subTotal);
    const deliveryPrice = isDeliveryEnabled ? parseFloat(deliveryCost) : 0;
    const deliveryPromotionPrice = manualDeliveryCost
      ? 0
      : shippingPromotionDiscount;
    const summaryDiscountsPrice = Math.abs(parseFloat(summaryDiscounts));
    const globalDiscountPrice = parseFloat(
      globalDiscount ? globalDiscount.discount : 0
    );

    const couponDiscount =
      coupon && !isDeliveryEnabled ? Math.abs(parseFloat(coupon.discount)) : 0;

    return (
      subTotalPrice +
      (deliveryPrice - deliveryPromotionPrice) -
      summaryDiscountsPrice -
      globalDiscountPrice -
      couponDiscount
    );
  }, [
    subTotal,
    total,
    isDeliveryEnabled,
    deliveryCost,
    manualDeliveryCost,
    shippingPromotionDiscount,
    summaryDiscounts,
    globalDiscount,
    coupon,
  ]);

  const handlePhoneError = useCallback((error?: string) => {
    setPhoneError(error);
  }, []);

  // const firstStepContinueDisabled = useMemo(() => {
  //   return (
  //     !total ||
  //     !deliveryMethodForm ||
  //     (deliveryMethodForm === 'SHIPPING' &&
  //       shippingMethod &&
  //       shippingMethod.storeShippingMethodType === 'FLAT_RATE' &&
  //       !(
  //         shippingMethod.enabledPromotions &&
  //         subTotal &&
  //         parseFloat(
  //           shippingMethod.nationalShippingPromotion?.minOrderAmount
  //         ) <= parseFloat(subTotal)
  //       ))
  //   );
  // }, [deliveryMethodForm, shippingMethod, subTotal, total]);

  const summaryLines = (
    <>
      <div className={classes.summaryLine}>
        <Typography className={classes.summaryText}>Subtotal</Typography>
        <Typography className={classes.summaryText}>
          {commaFormat(subTotalCost || '--', currency ? currency.symbol : '')}
        </Typography>
      </div>
      {promotionApplieds.length && !coupon
        ? promotionApplieds.map(p => (
            <div key={`promo-${p.name}`} className={classes.summaryLine}>
              <Typography
                className={`${classes.summaryText} ${classes.summaryTextCoupon}`}
              >
                {p.name}
              </Typography>
              <div style={{display: 'flex'}}>
                <Typography
                  className={`${classes.summaryText} ${classes.summaryTextCoupon}`}
                >
                  -{commaFormat(p.discount, currency ? currency.symbol : '')}
                </Typography>
              </div>
            </div>
          ))
        : null}
      {coupon && coupon.code && coupon.discount ? (
        <div className={classes.summaryLine}>
          <Typography
            className={`${classes.summaryText} ${classes.summaryTextCoupon}`}
          >
            {coupon.code}
          </Typography>
          <div style={{display: 'flex'}}>
            <Typography
              className={`${classes.summaryText} ${classes.summaryTextCoupon}`}
            >
              -{commaFormat(coupon.discount, currency ? currency.symbol : '')}
            </Typography>
          </div>
        </div>
      ) : null}
      {isDeliveryEnabled && (
        <div className={classes.summaryLine}>
          <Typography className={classes.summaryText}>Envío</Typography>
          <Typography className={classes.summaryText}>
            {commaFormat(deliveryCost || '--', currency ? currency.symbol : '')}
          </Typography>
        </div>
      )}
      {isDeliveryEnabled &&
        !manualDeliveryCost &&
        shippingPromotionDiscount > 0 && (
          <div className={classes.summaryLine}>
            <Typography className={classes.textPurple}>
              Promoción de envío
            </Typography>
            <Typography className={classes.shoppingCartSubtotal}>
              -{commaFormat(shippingPromotionDiscount, currency.symbol)}
            </Typography>
          </div>
        )}

      {globalDiscount && (
        <div className={classes.row}>
          <Typography className={classes.textPurple}>Descuento</Typography>
          <Typography className={classes.shoppingCartSubtotal}>
            -{commaFormat(globalDiscount.discount, currency.symbol)}
          </Typography>
        </div>
      )}
      <div className={classes.summaryLine}>
        <Typography className={`${classes.summaryText} ${classes.bold}`}>
          Total
        </Typography>
        <Typography className={`${classes.summaryText} ${classes.bold}`}>
          {commaFormat(totalCost || '--', currency ? currency.symbol : '')}
        </Typography>
      </div>
    </>
  );

  const disableInput = isExecutingCheckout;

  if (!shoppingCart || !shoppingCart.items.length) {
    return <Redirect to="/orders/manual-order" />;
  }

  const handleSelectOption = (customer: CrmCustomer) => {
    if (!customer) return;
    const {fullName, email, phone, identificationNumber, addressRaw} = customer;
    const [firstName, lastName] = fullName.split(' ');

    setFirstName({value: firstName, fromAutoComplete: true});
    setValue('lastName', lastName);
    if (identificationNumber) {
      setValue('idNumber', identificationNumber);
    }
    setValue('email', email);

    if (addressRaw) {
      setValue('address1', addressRaw.address1);
      setValue('address2', addressRaw.address2);
    }

    setPhone(phone);
  };

  const preserveDataWhenReturnToPrevStep = () => {
    if (!customerSelected) return;
    const {fullName, email, identificationNumber} = customerSelected;
    const [, lastName] = fullName.split(' ');

    setValue('lastName', lastName);
    if (identificationNumber) {
      setValue('idNumber', identificationNumber);
    }
    setValue('email', email);
  };

  const getCustomerLabel = (customer: CrmCustomer) => {
    const orders = customer.numberOfOrders ? customer.numberOfOrders : 0;
    const {fullName, phone} = customer;
    return `${fullName} | ${phone} | ${orders} ${
      orders === 1 ? 'orden' : 'ordenes'
    }`;
  };

  const checkoutDetailMobileClasses = `${classes.checkoutCartFooterMobile} ${
    checkoutMobileOpen ? classes.checkoutCartFooterMobileOpen : ''
  }`;

  const buildCheckoutInput = (elm: CheckoutInput) => {
    const type = elm.inputType;
    switch (type) {
      case INPUT_TYPE.NUMBER:
        return <NumberInput register={register} errors={errors} item={elm} />;
      case INPUT_TYPE.TEXT:
        return <TextInput register={register} errors={errors} item={elm} />;
      case INPUT_TYPE.CHECKLIST:
        return <ChecklistInput errors={errors} classes={classes} item={elm} />;
      case INPUT_TYPE.DATE_PICKER:
        return <CalendarInput item={elm} classes={classes} />;
      default:
        return null;
    }
  };

  const handleInfoStep = data => {
    setAdditionalInformationValidate(true);
    const isValidCustomInputs = isValidCustomAdditionalInformation();
    if (!isValidCustomInputs) return;

    if (!phone) {
      setPhoneError('El teléfono es requerido');
      return;
    }

    console.log({data});

    const country = data[DELIVERY_FORM_FIELDS.COUNTRY] || storeCountry;
    formInformationValues.current = {
      ...data,
      country,
    };
    setStep(1);
  };

  const buildDeliveryLabel = () => {
    if (step === 0) return '';

    const formValues = formInformationValues.current;
    const label = new StringMutable();
    const address1 = formValues[DELIVERY_FORM_FIELDS.ADDRESS_1];
    const address2 = formValues[DELIVERY_FORM_FIELDS.ADDRESS_2];
    const zipCode = formValues[DELIVERY_FORM_FIELDS.ZIP_CODE];
    const country = formValues[DELIVERY_FORM_FIELDS.COUNTRY]?.name;
    const admin1 = formValues[DELIVERY_FORM_FIELDS.ADMIN1]?.adminName;
    const admin2 = formValues[DELIVERY_FORM_FIELDS.ADMIN2]?.adminName;
    const admin3 = formValues[DELIVERY_FORM_FIELDS.ADMIN3]?.adminName;

    if (address1) label.concat(address1);
    if (address2) label.concat(', ', address2);
    if (zipCode) label.concat(', ', zipCode);
    if (country) label.concat(', ', country);
    if (admin3) label.concat(', ', admin3);
    if (admin2) label.concat(', ', admin2);
    if (admin1) label.concat(', ', admin1);

    return label.toString();
  };

  const deliveryLabel = buildDeliveryLabel();

  const buildPickupLabel = () => {
    if (step === 0) return '';

    const formValues = formInformationValues.current;
    const label = new StringMutable();
    const pickupMethod = formValues[DELIVERY_FORM_FIELDS.PICKUP_METHOD];

    if (!pickupMethod) return '';

    const {
      address1,
      address2,
      zipCode,
      country,
      admin1,
      admin2,
      admin3,
    } = pickupMethod;

    if (address1) label.concat(address1);
    if (address2) label.concat(', ', address2);
    if (zipCode) label.concat(', ', zipCode);
    if (country) label.concat(', ', country.name);
    if (admin3) label.concat(', ', admin3.admin3Name);
    if (admin2) label.concat(', ', admin2.admin2Name);
    if (admin1) label.concat(', ', admin1.admin1Name);

    return label.toString();
  };

  const pickupLabel = buildPickupLabel();

  const clearDeliveryFormData = () => {
    setValue(DELIVERY_FORM_FIELDS.ADDRESS_1, undefined);
    setValue(DELIVERY_FORM_FIELDS.ADDRESS_2, undefined);
    setValue(DELIVERY_FORM_FIELDS.ADMIN1, undefined);
    setValue(DELIVERY_FORM_FIELDS.ADMIN2, undefined);
    setValue(DELIVERY_FORM_FIELDS.ADMIN3, undefined);
  };

  const handleSelectCustomer = (event, newValue) => {
    setCustomerDeliveryData(null);
    clearDeliveryFormData();

    handleSelectOption(newValue);
    setCustomerSelected(newValue);

    if (!newValue.addressRaw) return;
    reset({
      [DELIVERY_FORM_FIELDS.DELIVERY_METHOD]: 'SHIPPING',
      [DELIVERY_FORM_FIELDS.ADDRESS_1]: newValue.addressRaw.address1,
      [DELIVERY_FORM_FIELDS.ADDRESS_2]: newValue.addressRaw.address2,
    });

    const countryCode = newValue.addressRaw.country ?? storeCountry.code;
    const admin1Name = newValue.addressRaw.admin1;
    const admin2Name = newValue.addressRaw.admin2;
    const admin3Name = newValue.addressRaw.admin3;

    const countryObject = shippingMethod?.availableShippingCountries.find(
      country => country.code === countryCode
    );

    setFetchingDeliveryData(true);

    getAdminData(countryCode, admin1Name, admin2Name, admin3Name)
      .then(adminData => {
        const data = {
          country: countryObject ?? storeCountry,
          address1: newValue.addressRaw.address1,
          address2: newValue.addressRaw.address2,
          admin1: adminData.admin1,
          admin2: adminData.admin2,
          admin3: adminData.admin3,
          id: '',
          type: 'shipping-address',
        };
        setCustomerDeliveryData(data);

        reset({
          [DELIVERY_FORM_FIELDS.DELIVERY_METHOD]: 'SHIPPING',
          [DELIVERY_FORM_FIELDS.ADDRESS_1]: newValue.addressRaw.address1,
          [DELIVERY_FORM_FIELDS.ADDRESS_2]: newValue.addressRaw.address2,
          [DELIVERY_FORM_FIELDS.COUNTRY]: countryObject,
          [DELIVERY_FORM_FIELDS.ADMIN1]: adminResponseToAdminAddress(
            adminData.admin1
          ),
          [DELIVERY_FORM_FIELDS.ADMIN2]: adminResponseToAdminAddress(
            adminData.admin2
          ),
          [DELIVERY_FORM_FIELDS.ADMIN3]: adminResponseToAdminAddress(
            adminData.admin3
          ),
        });
      })
      .catch(() => {
        setCustomerDeliveryData(null);
      })
      .finally(() => {
        setFetchingDeliveryData(false);
      });
  };

  return (
    <FormProvider {...methods}>
      <Switch>
        {step === 0 && (
          <>
            <SnackBar
              mode="error"
              message={phoneError || ''}
              // handleClose={() => setPhoneError('')}
            />
            <div className={classes.headerWrapper}>
              <Typography className={classes.title}>
                <IconButton
                  className={classes.backButton}
                  onClick={() => {
                    resetProductsState();
                    history.push('/orders/manual-order');
                  }}
                >
                  <ArrowBack />
                </IconButton>
                Crear Pedido
              </Typography>
            </div>
            {userHasPreOrderFeature && (
              <Collapsible
                className={classes.preOrder}
                startIcon={
                  <img src={searchClient} alt="" style={{marginRight: 16}} />
                }
                title="¿No tienes los datos de tu cliente en este momento?"
                paddingH={2}
              >
                <div className={classes.preOrderContent}>
                  <Typography className={classes.preOrderText}>
                    No te preocupes, crea un pre pedido con los productos y
                    envíale el link a tu cliente para que pueda llenar sus
                    datos.
                  </Typography>
                  <CustomButton
                    className={classes.preOrderButton}
                    onClick={handleCreatePreOrder}
                  >
                    Crear pre pedido
                  </CustomButton>
                </div>
              </Collapsible>
            )}
            <main className={classes.content}>
              <div className={classes.columnContainer}>
                <Collapsible
                  className={classes.contentCollapsible}
                  title={
                    <div className={classes.sectionTitleWrapper}>
                      <Typography className={classes.sectionTitle}>
                        Información del Cliente
                      </Typography>
                    </div>
                  }
                  paddingH={2}
                >
                  <Autocomplete
                    freeSolo
                    className={classes.textField}
                    options={customers}
                    onChange={handleSelectCustomer}
                    value={customerSelected}
                    getOptionLabel={option => {
                      if (!option.phone) return '';
                      return option.phone;
                    }}
                    noOptionsText="Usuario no encontrado"
                    renderOption={(option, state) => {
                      const {selected} = state;

                      const label = getCustomerLabel(option);

                      const optClasses = `${classes.option} ${
                        selected ? classes.optionSelected : ''
                      }`;

                      return <span className={optClasses}>{label}</span>;
                    }}
                    renderInput={({dispatch, ...params}) => {
                      return (
                        <ControlledTelInput
                          {...params}
                          value={phone}
                          label="Número telefónico"
                          fullWidth
                          className={classes.textField}
                          name="phone"
                          defaultCountry={country}
                          onError={handlePhoneError}
                          onChange={(value, number) => {
                            setPhoneToSearch(value);
                            setPhone(value);
                          }}
                        />
                      );
                    }}
                  />
                  <Autocomplete
                    freeSolo
                    className={classes.textField}
                    onChange={handleSelectCustomer}
                    value={customerSelected}
                    options={customers}
                    getOptionLabel={option => {
                      if (!option.fullName) return '';
                      const [name] = option.fullName.split(' ');
                      return name;
                    }}
                    noOptionsText="Usuario no encontrado"
                    renderOption={(option, state) => {
                      const {selected} = state;

                      const label = getCustomerLabel(option);

                      const optClasses = `${classes.option} ${
                        selected ? classes.optionSelected : ''
                      }`;

                      return <span className={optClasses}>{label}</span>;
                    }}
                    renderInput={({dispatch, ...params}) => {
                      return (
                        <CustomTextField
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search />
                              </InputAdornment>
                            ),
                          }}
                          label="Nombres"
                          name="firstName"
                          // defaultValue={firstName.value}
                          onChange={e => {
                            setFirstName({
                              value: e.target.value,
                              fromAutoComplete: false,
                            });
                            firstNameRef.current = true;
                          }}
                          type="text"
                          disabled={disableInput}
                          error={firstNameRef.current && !firstName.value}
                          helperText={
                            firstNameRef.current && !firstName.value
                              ? 'Campo obligatorio'
                              : ''
                          }
                        />
                      );
                    }}
                  />
                  <CustomTextField
                    label="Apellidos"
                    name="lastName"
                    className={classes.textField}
                    defaultValue={tempOrderInfo ? tempOrderInfo.lastName : ''}
                    error={!!errors.lastName}
                    helperText={errors.lastName ? errors.lastName.message : ''}
                    inputRef={register({
                      required: 'Campo obligatorio',
                    })}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type="text"
                    disabled={disableInput}
                  />
                  <CustomTextField
                    label="Documento de identidad (opcional)"
                    name="idNumber"
                    className={classes.textField}
                    defaultValue={tempOrderInfo ? tempOrderInfo.idNumber : ''}
                    error={!!errors.idNumber}
                    helperText={errors.idNumber ? errors.idNumber.message : ''}
                    inputRef={register()}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type="text"
                    disabled={disableInput}
                  />
                  <CustomTextField
                    label="Correo electrónico"
                    name="email"
                    className={classes.textField}
                    defaultValue={tempOrderInfo ? tempOrderInfo.email : ''}
                    error={!!errors.email}
                    helperText={errors.email ? errors.email.message : ''}
                    inputRef={register({
                      required: 'Campo obligatorio',
                      pattern: {
                        value: REGEX.email,
                        message: 'Formato de email inválido',
                      },
                    })}
                    type="text"
                    disabled={disableInput}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Collapsible>

                {isDeliveryEnabled && (
                  <Collapsible
                    className={classes.contentCollapsible}
                    title="Método de entrega"
                    paddingH={2}
                  >
                    {customerDeliveryData && (
                      <DeliveryForm
                        defaultDeliveryAddress={customerDeliveryData}
                        defaultDeliveryMethod="SHIPPING"
                      />
                    )}
                    {!customerDeliveryData && <DeliveryForm />}
                    {fetchingDeliveryData && (
                      <div className={classes.loader}>
                        <InlineLoader loading size={20} />
                      </div>
                    )}
                  </Collapsible>
                )}
                <Hidden smUp>
                  {hasTaxInvoice && (
                    <label className={classes.contentColumn}>
                      <div className={classes.sectionHeader}>
                        <span className={classes.subtitle}>
                          Llenar datos de Facturación
                        </span>
                        <IosSwitch
                          value={taxFormOpen}
                          onChange={handleTaxFormOpenChange}
                        />
                      </div>
                    </label>
                  )}
                  {taxFormOpen && (
                    <TaxInvoiceInformationForm
                      manualOrderShippingData={manualOrderShippingData}
                    />
                  )}
                </Hidden>
                {filteredAdditionalInformation && (
                  <Collapsible
                    className={classes.contentCollapsible}
                    title={
                      <div className={classes.sectionTitleWrapper}>
                        <Typography className={classes.sectionTitle}>
                          Información adicional
                        </Typography>
                        <Typography className={classes.sectionOptional}>
                          (opcional)
                        </Typography>
                      </div>
                    }
                    paddingH={2}
                  >
                    <Grid container spacing={2}>
                      {filteredAdditionalInformation.map(elem => (
                        <Grid item xs={12}>
                          {buildCheckoutInput(elem)}
                        </Grid>
                      ))}
                    </Grid>
                  </Collapsible>
                )}
              </div>
              <Hidden xsDown>
                <div className={classes.columnContainer}>
                  {shoppingCartItemsSection}
                  {hasTaxInvoice && (
                    <label className={classes.contentColumn}>
                      <div className={classes.sectionHeader}>
                        <span className={classes.subtitle}>
                          Llenar datos de Facturación
                        </span>
                        <IosSwitch
                          value={taxFormOpen}
                          onChange={handleTaxFormOpenChange}
                        />
                      </div>
                    </label>
                  )}
                  {taxFormOpen && (
                    <TaxInvoiceInformationForm
                      manualOrderShippingData={manualOrderShippingData}
                    />
                  )}
                  <div className={classes.contentColumn}>
                    {summaryLines}
                    <CustomButton
                      variant="contained"
                      color="primary"
                      className={classes.continueButton}
                      onClick={handleSubmit(handleInfoStep)}
                      disabled={Boolean(!firstName.value || phoneError)}
                    >
                      Continuar
                    </CustomButton>
                  </div>
                  {/* {couponSection} */}
                </div>
              </Hidden>
              <Hidden smUp>
                <div className={checkoutDetailMobileClasses}>
                  {checkoutMobileOpen ? (
                    <>
                      <div className={classes.sectionTitleSummary}>
                        <Typography className={classes.sectionTitle}>
                          Resumen de Productos www
                        </Typography>
                        <div className={classes.shoppingCartItemsAmount}>
                          {shoppingCartItemsAmount}
                        </div>
                        <IconButton
                          className={classes.footerMobileCollapse}
                          onClick={closeCheckoutMobile}
                        >
                          <KeyboardArrowDown />
                        </IconButton>
                      </div>
                      <div className={classes.itemContainer}>
                        {shoppingCart.items.map((item: ShoppingCartItem, i) => {
                          let productPrice = {
                            unitPrice: getProductItemOriginalPrice(item),
                          };

                          if (item && itemPrices && itemPrices.length) {
                            const index = itemPrices.findIndex(price =>
                              item.productVariant
                                ? price.item.productVariant ===
                                  item.productVariant.id
                                : price.item.product === item.product.id
                            );
                            const drafItem = itemPrices[index];
                            if (index >= 0 && drafItem.item.price)
                              productPrice = drafItem.item.price;
                          }
                          return (
                            <React.Fragment key={i}>
                              <div className={`${classes.shoppingCartItem}`}>
                                <img
                                  className={classes.shoppingCartProductImage}
                                  src={getImageUrl(
                                    item.productVariant
                                      ? item.productVariant.imageId
                                      : item.product.mainImageId
                                  )}
                                  alt=""
                                />
                                <div
                                  className={classes.shoppingCartProductDetail}
                                >
                                  <Typography
                                    className={classes.shoppingCartProductName}
                                  >
                                    {item.product.name}
                                  </Typography>
                                  {item.productVariant &&
                                    item.productVariant.options && (
                                      <Typography
                                        className={
                                          classes.shoppingCartProductVariant
                                        }
                                      >
                                        {Object.values(
                                          item.productVariant.options
                                        ).join('/')}
                                      </Typography>
                                    )}
                                  <Typography
                                    className={
                                      classes.shoppingCartProductAmount
                                    }
                                  >
                                    Cantidad: {item.amount}
                                  </Typography>
                                </div>
                                <div
                                  className={classes.shoppingCartItemActions}
                                >
                                  <div />
                                  <div
                                    className={classes.shoppingCartProductPrice}
                                  >
                                    {parseFloat(productPrice.unitPrice) <
                                      (item.productVariant
                                        ? parseFloat(item.productVariant.price)
                                        : parseFloat(item.product.price)) && (
                                      <Typography
                                        className={
                                          classes.shoppingCartProductPriceDiscounted
                                        }
                                      >
                                        {commaFormat(
                                          (item.productVariant
                                            ? parseFloat(
                                                item.productVariant.price
                                              )
                                            : parseFloat(item.product.price)) *
                                            item.amount,
                                          currency ? currency.symbol : ''
                                        )}
                                      </Typography>
                                    )}
                                    <Typography
                                      className={
                                        classes.shoppingCartProductPriceNormal
                                      }
                                    >
                                      {productPrice.source === 'BULK_PRICE' && (
                                        <Tooltip
                                          title="Precio al por mayor aplicado"
                                          arrow
                                        >
                                          <div
                                            className={
                                              classes.shoppingCartProductBulkPriceFlag
                                            }
                                          >
                                            <LocalOfferOutlined fontSize="small" />
                                          </div>
                                        </Tooltip>
                                      )}
                                      {item.product && item.amount
                                        ? commaFormat(
                                            parseFloat(productPrice.unitPrice) *
                                              item.amount,
                                            currency ? currency.symbol : ''
                                          )
                                        : 0}
                                    </Typography>
                                  </div>
                                </div>
                              </div>
                              {i + 1 < shoppingCart.items.length && (
                                <Divider className={classes.divider} />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                      {summaryLines}
                    </>
                  ) : (
                    <>
                      <div className={classes.row}>
                        <Typography
                          variant="body2"
                          className={classes.footerMobileText}
                        >
                          Tienes {shoppingCartItemsAmount} productos en tu
                          carrito
                        </Typography>
                        <IconButton
                          className={classes.footerMobileCollapse}
                          onClick={openCheckoutMobile}
                        >
                          <KeyboardArrowUp />
                        </IconButton>
                      </div>
                      <div className={classes.row}>
                        <Typography className={classes.checkoutTotal}>
                          Total
                        </Typography>
                        <Typography className={classes.checkoutTotal}>
                          {total && shoppingCart.items.length
                            ? commaFormat(totalCost, currency.symbol)
                            : '--'}
                        </Typography>
                      </div>
                    </>
                  )}
                  <CustomButton
                    variant="contained"
                    color="primary"
                    className={classes.continueButton}
                    onClick={handleSubmit(handleInfoStep)}
                    disabled={!firstName.value || phoneError}
                  >
                    Continuar
                  </CustomButton>
                </div>
              </Hidden>
            </main>
            <CustomModal
              open={confirmPreOrderModal}
              handleCloseModal={() => setConfirmPreOrderModal(false)}
            >
              <div className={classes.createPreOrderModal}>
                <Typography className={classes.createPreOrderModalText}>
                  ¿Crear un pre pedido con los productos seleccionados?
                </Typography>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    marginTop: 16,
                  }}
                >
                  <CustomButton
                    variant="outlined"
                    style={{padding: '4px 32px'}}
                    onClick={() => setConfirmPreOrderModal(false)}
                  >
                    No
                  </CustomButton>
                  <CustomButton
                    variant="contained"
                    color="primary"
                    style={{padding: '4px 32px'}}
                    disabled={isExecutingCheckout}
                    onClick={confirmCreatePreOrder}
                  >
                    Sí
                  </CustomButton>
                </div>
              </div>
            </CustomModal>
          </>
        )}

        {step === 1 && (
          <>
            <div className={classes.headerWrapper}>
              <Typography className={classes.title}>
                <IconButton
                  className={classes.backButton}
                  onClick={() => {
                    // history.push('/orders/manual-order/checkout/info');
                    setStep(0);
                    preserveDataWhenReturnToPrevStep();
                  }}
                >
                  <ArrowBack />
                </IconButton>
                Crear Pedido
              </Typography>
            </div>
            {isExecutingCheckout && <LoadingSpinner />}
            <main className={classes.content}>
              <div className={classes.columnContainer}>
                {formInformationValues.current && (
                  <Collapsible
                    className={classes.contentCollapsible}
                    title="Información de contacto, Dirección de envío e Información adicional"
                    paddingH={2}
                    startClosed={true}
                  >
                    <Typography className={classes.summarySubtitle}>
                      Información de contacto
                    </Typography>

                    <Typography className={classes.summaryInfo}>{`${
                      firstName.value
                    } ${formInformationValues.current.lastName}${
                      formInformationValues.current.idNumber
                        ? `, ${formInformationValues.current.idNumber}`
                        : ''
                    }`}</Typography>
                    <Typography className={classes.summaryInfo}>
                      {formInformationValues.current.email}
                    </Typography>
                    <Typography className={classes.summaryInfo}>
                      {phone &&
                      phone.length > 5 &&
                      parsePhoneNumber(`+${phone}`)
                        ? parsePhoneNumber(`+${phone}`).formatInternational()
                        : `+${phone}`}
                    </Typography>

                    {formInformationValues.current.deliveryMethod ===
                      'PICK_UP' && (
                      <>
                        <Typography className={classes.summarySubtitle}>
                          Recojo en tienda
                        </Typography>
                        <Typography className={classes.summaryInfo}>
                          {formInformationValues.current[
                            DELIVERY_FORM_FIELDS.PICKUP_METHOD
                          ]?.displayName || ''}
                        </Typography>
                        <Typography className={classes.summaryInfo}>
                          {pickupLabel}
                        </Typography>
                        <Typography className={classes.summaryInfo}>
                          {
                            formInformationValues.current[
                              DELIVERY_FORM_FIELDS.PICKUP_METHOD
                            ]?.description
                          }
                        </Typography>
                      </>
                    )}
                    {formInformationValues.current.deliveryMethod ===
                      'SHIPPING' && (
                      <>
                        <Typography className={classes.summarySubtitle}>
                          Delivery
                        </Typography>
                        <Typography className={classes.summaryInfo}>
                          {deliveryLabel}
                        </Typography>
                      </>
                    )}
                  </Collapsible>
                )}
                <Collapsible
                  className={classes.contentCollapsible}
                  title="Método de Pago"
                  paddingH={2}
                >
                  <PaymentForm
                    paymentMethodSelected={paymentMethodSelected}
                    setPaymentMethodSelected={setPaymentMethodSelected}
                  />
                </Collapsible>

                <Hidden smUp>
                  <div className={checkoutDetailMobileClasses}>
                    {checkoutMobileOpen ? (
                      <>
                        <div className={classes.sectionTitleSummary}>
                          <Typography className={classes.sectionTitle}>
                            Resumen de Productos
                          </Typography>
                          <div className={classes.shoppingCartItemsAmount}>
                            {shoppingCartItemsAmount}
                          </div>
                          <IconButton
                            className={classes.footerMobileCollapse}
                            onClick={closeCheckoutMobile}
                          >
                            <KeyboardArrowDown />
                          </IconButton>
                        </div>
                        <div className={classes.itemContainer}>
                          {shoppingCart.items.map(
                            (item: ShoppingCartItem, i) => {
                              let productPrice = {
                                unitPrice: getProductItemOriginalPrice(item),
                              };

                              if (item && itemPrices && itemPrices.length) {
                                const index = itemPrices.findIndex(price =>
                                  item.productVariant
                                    ? price.item.productVariant ===
                                      item.productVariant.id
                                    : price.item.product === item.product.id
                                );
                                const draftItem = itemPrices[index];
                                if (index >= 0 && draftItem.item.price)
                                  productPrice = draftItem.item.price;
                              }
                              return (
                                <React.Fragment key={i}>
                                  <div
                                    className={`${classes.shoppingCartItem}`}
                                  >
                                    <img
                                      className={
                                        classes.shoppingCartProductImage
                                      }
                                      src={getImageUrl(
                                        item.productVariant
                                          ? item.productVariant.imageId
                                          : item.product.mainImageId
                                      )}
                                      alt=""
                                    />
                                    <div
                                      className={
                                        classes.shoppingCartProductDetail
                                      }
                                    >
                                      <Typography
                                        className={
                                          classes.shoppingCartProductName
                                        }
                                      >
                                        {item.product.name}
                                      </Typography>
                                      {item.productVariant &&
                                        item.productVariant.options && (
                                          <Typography
                                            className={
                                              classes.shoppingCartProductVariant
                                            }
                                          >
                                            {Object.values(
                                              item.productVariant.options
                                            ).join('/')}
                                          </Typography>
                                        )}
                                      <Typography
                                        className={
                                          classes.shoppingCartProductAmount
                                        }
                                      >
                                        Cantidad: {item.amount}
                                      </Typography>
                                    </div>
                                    <div
                                      className={
                                        classes.shoppingCartItemActions
                                      }
                                    >
                                      <div />
                                      <div
                                        className={
                                          classes.shoppingCartProductPrice
                                        }
                                      >
                                        {parseFloat(productPrice.unitPrice) <
                                          (item.productVariant
                                            ? parseFloat(
                                                item.productVariant.price
                                              )
                                            : parseFloat(
                                                item.product.price
                                              )) && (
                                          <Typography
                                            className={
                                              classes.shoppingCartProductPriceDiscounted
                                            }
                                          >
                                            {commaFormat(
                                              (item.productVariant
                                                ? parseFloat(
                                                    item.productVariant.price
                                                  )
                                                : parseFloat(
                                                    item.product.price
                                                  )) * item.amount,
                                              currency ? currency.symbol : ''
                                            )}
                                          </Typography>
                                        )}
                                        <Typography
                                          className={
                                            classes.shoppingCartProductPriceNormal
                                          }
                                        >
                                          {productPrice.source ===
                                            'BULK_PRICE' && (
                                            <Tooltip
                                              title="Precio al por mayor aplicado"
                                              arrow
                                            >
                                              <div
                                                className={
                                                  classes.shoppingCartProductBulkPriceFlag
                                                }
                                              >
                                                <LocalOfferOutlined fontSize="small" />
                                              </div>
                                            </Tooltip>
                                          )}
                                          {item.product && item.amount
                                            ? commaFormat(
                                                parseFloat(
                                                  productPrice.unitPrice
                                                ) * item.amount,
                                                currency ? currency.symbol : ''
                                              )
                                            : 0}
                                        </Typography>
                                      </div>
                                    </div>
                                  </div>
                                  {i + 1 < shoppingCart.items.length && (
                                    <Divider className={classes.divider} />
                                  )}
                                </React.Fragment>
                              );
                            }
                          )}
                        </div>
                        <div className={classes.row}>
                          <Typography
                            variant="body2"
                            className={classes.footerMobileText}
                          >
                            Tienes {shoppingCartItemsAmount} productos en tu
                            carrito
                          </Typography>
                          <IconButton
                            className={classes.footerMobileCollapse}
                            onClick={openCheckoutMobile}
                          >
                            <KeyboardArrowUp />
                          </IconButton>
                        </div>
                        <div className={classes.row}>
                          <Typography className={classes.checkoutTotal}>
                            Total
                          </Typography>
                          <Typography className={classes.checkoutTotal}>
                            {total && shoppingCart.items.length
                              ? commaFormat(totalCost, currency.symbol)
                              : '--'}
                          </Typography>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={classes.row}>
                          <Typography
                            variant="body2"
                            className={classes.footerMobileText}
                          >
                            Tienes {shoppingCartItemsAmount} productos en tu
                            carrito
                          </Typography>
                          <IconButton
                            className={classes.footerMobileCollapse}
                            onClick={openCheckoutMobile}
                          >
                            <KeyboardArrowUp />
                          </IconButton>
                        </div>
                        <div className={classes.row}>
                          <Typography className={classes.checkoutTotal}>
                            Total
                          </Typography>
                          <Typography className={classes.checkoutTotal}>
                            {total && shoppingCart.items.length
                              ? commaFormat(totalCost, currency.symbol)
                              : '--'}
                          </Typography>
                        </div>
                      </>
                    )}
                  </div>
                  <CustomButton
                    variant="contained"
                    color="primary"
                    fullWidth
                    className={classes.continueButton}
                    onClick={handleSubmit(handleCreateOrder)}
                    disabled={!paymentMethodSelected || disableInput}
                  >
                    Continuar
                  </CustomButton>
                </Hidden>
              </div>
              <Hidden xsDown>
                <div className={classes.columnContainer}>
                  {shoppingCartItemsSection}
                  <div className={classes.contentColumn}>
                    {summaryLines}
                    <CustomButton
                      variant="contained"
                      color="primary"
                      className={classes.continueButton}
                      onClick={handleSubmit(handleCreateOrder)}
                      disabled={!paymentMethodSelected || disableInput}
                    >
                      Continuar
                    </CustomButton>
                  </div>
                  {/* {couponSection} */}
                </div>
              </Hidden>
            </main>
          </>
        )}
      </Switch>
    </FormProvider>
  );
}
